/**
 * Tenant Context - Provides current tenant information throughout the app
 */
import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { getSubdomain, isMultiTenantMode } from '../utils/tenant';

const TenantContext = createContext();

export const useTenant = () => {
  const context = useContext(TenantContext);
  if (!context) {
    throw new Error('useTenant must be used within TenantProvider');
  }
  return context;
};

export const TenantProvider = ({ children }) => {
  const { isAuthenticated, getAccessTokenSilently } = useAuth0();
  const [tenant, setTenant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const subdomain = getSubdomain();

  useEffect(() => {
    const fetchTenantInfo = async () => {
      // If no subdomain, we're on localhost - skip tenant fetch
      if (!subdomain) {
        setTenant({
          subdomain: 'local',
          name: 'Local Development',
          isLocal: true
        });
        setLoading(false);
        return;
      }

      // If authenticated, fetch tenant info from API
      if (isAuthenticated) {
        try {
          const token = await getAccessTokenSilently();
          const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

          const response = await fetch(`${API_URL}/tenants/current`, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });

          if (response.ok) {
            const data = await response.json();
            setTenant({
              ...data,
              isLocal: false
            });
          } else {
            console.error('Failed to fetch tenant info:', response.status);
            setError('Failed to load tenant information');
          }
        } catch (err) {
          console.error('Error fetching tenant info:', err);
          setError(err.message);
        }
      } else {
        // Not authenticated yet, just set subdomain
        setTenant({
          subdomain: subdomain,
          name: subdomain,
          isLocal: false
        });
      }

      setLoading(false);
    };

    fetchTenantInfo();
  }, [isAuthenticated, subdomain, getAccessTokenSilently]);

  const value = {
    tenant,
    subdomain,
    loading,
    error,
    isMultiTenant: isMultiTenantMode(),
  };

  return (
    <TenantContext.Provider value={value}>
      {children}
    </TenantContext.Provider>
  );
};

export default TenantContext;
